/**
 * Fixtures behind the compliance adapter seam (SE-7002, FC-1.1).
 *
 * There is no FC backend yet — no schema, no resolvers, no populated regime
 * data. Everything the adapter serves today comes from here, and when the
 * backend lands only the adapter changes.
 *
 * The DEFAULT fixture is deliberately EMPTY. Even after the schema deploys, a
 * club with zero configured rules is what will actually be live first, so the
 * unconfigured verdict is the primary path rather than an edge case. Shipping
 * a populated fixture as the default would put invented compliance numbers in
 * front of a user, which is worse than showing nothing.
 *
 * The populated fixtures exist for the gallery and the tests, which need the
 * OK and BREACH branches to be reachable.
 * @module features/financial-compliance/adapters/complianceFixtures
 */

import type {
  ClubFinancialBaseline,
  ComplianceRuleConfig,
} from "../domain/evaluateRules";

/**
 * The live default: nothing configured.
 *
 * Every layer renders as "not configured" and the verdict is
 * `not-configured` — never a green "compliant", which would be a claim the
 * client is in no position to make.
 */
export const EMPTY_RULE_CONFIG: ComplianceRuleConfig = {};

/**
 * The live default baseline: nothing known.
 *
 * The deltas the panel shows are still real — they come from the terms the
 * user just typed — but nothing is asserted about the club's existing
 * position, because the client has not been told it.
 */
export const EMPTY_CLUB_BASELINE: ClubFinancialBaseline = {
  wagePerYear: 0,
  amortisationPerYear: 0,
  netBookValue: 0,
  squadSize: 0,
  revenuePerYear: 0,
};

/**
 * A configured regime — the gallery and tests exercise the OK/BREACH
 * branches with this, and `useLiveImpact` (SE-7002) takes it as the club's
 * current rules too (SE-7583).
 *
 * There is no per-club backend read yet — same reason `EMPTY_RULE_CONFIG`
 * above is a fixture rather than a query — so this is the one regime every
 * surface that needs "what has this club actually configured" shares today,
 * the Key Metrics grid's own fixture (`fixtures/complianceFixtures.ts`)
 * included. Before this was wired into `useLiveImpact`, the action modals'
 * Live Impact panel fell through to `EMPTY_RULE_CONFIG` unconditionally, so
 * it reported every rule "not configured" regardless of what the grid right
 * next to it showed — the modal was never told the club had configured
 * anything at all. The two surfaces are still two independent fixtures, so an
 * exact number (e.g. the grid's own "88.4%") is not guaranteed to match the
 * modal's cumulative recompute; only real backend rule evaluation unifies
 * them for good.
 */
export const CONFIGURED_RULE_CONFIG: ComplianceRuleConfig = {
  squadCostRatio: { capRatio: 0.7, warnRatio: 0.6 },
  wageBudget: { budgetPerYear: 120_000_000, warnFraction: 0.9 },
  registration: { maxSquadSize: 25 },
};

/**
 * A club position that a mid-size signing pushes into breach.
 * @see {@link CONFIGURED_RULE_CONFIG} — used together as this club's regime.
 */
export const CONFIGURED_CLUB_BASELINE: ClubFinancialBaseline = {
  wagePerYear: 110_000_000,
  amortisationPerYear: 40_000_000,
  netBookValue: 260_000_000,
  // At the registration limit already, so a signing tips every layer at once —
  // which is the state the breach frame illustrates.
  squadSize: 25,
  revenuePerYear: 220_000_000,
};
